import {Product, ProductRole, ProductRoleFull} from './../types';
import {ProductRoles, WorkspaceVersion} from './constants';
import {DisplayRole, OrgRole, OrgRoles, ProductRoleName, SystemRole} from './rolesV2';

function getEnumKey<T extends string>(enumObject: {[key: string]: T}, value: T): string | undefined {
  for (const key of Object.keys(enumObject)) {
    if (enumObject[key] === value) {
      return key;
    }
  }

  return undefined;
};

export const toDisplayRole = (role: OrgRole): DisplayRole | undefined => {
  switch (role) {
    case OrgRole.OWNER:
      return DisplayRole.OWNER;
    case OrgRole.ADMIN:
      return DisplayRole.ADMIN;
    case OrgRole.IAM_ADMIN:
      return DisplayRole.IAM_ADMIN;
    case OrgRole.USER:
      return DisplayRole.USER;
    case OrgRole.BILLING_READER:
      return DisplayRole.BILLING_READER;
    default:
      return;
  }
};

const displayRoleLookup = DisplayRole as {[key: string]: DisplayRole};
const orgRoleLookup = OrgRole as {[key: string]: OrgRole};

export const toOrgRole = (value: OrgRoles): OrgRole | undefined => {
  if (typeof value === 'string') {
    if (Object.values(OrgRole).includes(value as OrgRole)) {
      return value as OrgRole;
    }
    const roleKey = getEnumKey(DisplayRole, value);

    if (roleKey && roleKey in OrgRole) {
      return orgRoleLookup[roleKey];
    }

    return;
  }
};

export const toOrgDisplayRole = (value: OrgRoles): DisplayRole | undefined => {
  if (typeof value === 'string') {
    if (Object.values(DisplayRole).includes(value as DisplayRole)) {
      return value as DisplayRole;
    }
    const roleKey = getEnumKey(OrgRole, value);

    if (roleKey && roleKey in DisplayRole) {
      return displayRoleLookup[roleKey];
    }

    return;
  }
};

const capitalize = (s: string) => (s && s[0].toUpperCase() + s.slice(1)) || '';

export const toProductRoleDisplay = (role: ProductRole) => {
  return role
    .split('_')
    .map((value) => capitalize(value))
    .join(' ');
};

export const productGroupToIDPGroupNames = (productRole: ProductRole) => {
  const productRoleDisplay = toProductRoleDisplay(productRole);

  return [`Cribl ${productRoleDisplay}`, `Cribl${productRoleDisplay.replace(' ', '')}`];
};

export const organizationGroupToIDPGroupNames = (role: OrgRole) => {
  // Handle OrgRole types
  if (Object.values(OrgRole).includes(role as OrgRole)) {
    const displayRole = toDisplayRole(role as OrgRole);
    return [`Cribl Organization ${displayRole}`, `CriblOrganization${displayRole?.replace(' ', '')}`];
  }
};

export const getProductFromProductRole = (productRole: ProductRole): Product => {
  return productRole.split('_')[0] as Product;
}; // Example: getProductFromProductRole(EdgeRole.ADMIN) returns 'edge'

export const getRoleFromProductRole = (productRole: ProductRole): string => {
  return productRole.split('_')[1] as Product;
}; // Example: getRoleFromProductRole(EdgeRole.ADMIN) returns 'admin'

export const getProductRoleFromProductRoleFull = (productRoleFull: ProductRoleFull): ProductRole => {
  return productRoleFull.productRole;
};
// Example: getProductRoleFromProductRoleFull({workspace: 'workspace1', productRole: EdgeRole.ADMIN})
// returns 'edge_admin'

export const getProductRoleWorkspaceFromProductRoleFull = (productRoleFull: ProductRoleFull): string => {
  return productRoleFull.workspace;
};
// Example: getProductRoleWorkspaceFromProductRoleFull({workspace: 'workspace1', productRole: EdgeRole.ADMIN})
// returns 'workspace1'

export const getAllNoAccessRoles = (): ProductRole[] => {
  return ProductRoles.filter((role) => role.endsWith(`_${ProductRoleName.NOACCESS}`));
}; // Example: getAllNoAccessRoles() returns ['edge_noaccess', 'search_noaccess', 'stream_noaccess', 'lake_noaccess']

export const getAllAdminRoles = (): ProductRole[] => {
  return ProductRoles.filter((role) => role.endsWith(`_${ProductRoleName.ADMIN}`));
}; // Example: getAllAdminRoles() returns ['edge_admin', 'search_admin', 'stream_admin', 'lake_admin']

export const getAllReaderRoles = (): ProductRole[] => {
  return ProductRoles.filter((role) => role.endsWith(`_${ProductRoleName.READER}`));
}; // Example: getAllReaderRoles() returns ['edge_reader', 'search_reader', 'stream_reader', 'lake_reader']

export const getAllUserRoles = (): ProductRole[] => {
  return ProductRoles.filter((role) => role.endsWith(`_${ProductRoleName.USER}`));
}; // Example: getAllUserRoles() returns [edge_user', 'search_user', 'stream_user']

export const getAllEditorRoles = (): ProductRole[] => {
  return ProductRoles.filter((role) => role.endsWith(`_${ProductRoleName.EDITOR}`));
}; // Example: getAllEditorRoles() returns ["search_editor", 'lake_editor']

export const formatWorkspaceVersion = (version: number, workspace: string): string => {
  /* formats it will take eg. 
     search:main/cheesy-breadthing or workspace:main/cheesy-breadthing or main/cheesy-breadthing
     search:main-cheesy-breadthing or workspace:main-cheesy-breadthing or main-cheesy-breadthing
     and return the format version requested
   */
  const firstDashIndex = workspace.indexOf('-');
  const firstSlashIndex = workspace.indexOf('/');

  const isVersion1Format = firstDashIndex !== -1 && (firstSlashIndex === -1 || firstDashIndex < firstSlashIndex);
  const isVersion2Format = firstSlashIndex !== -1 && (firstDashIndex === -1 || firstSlashIndex < firstDashIndex);

  if ((version === WorkspaceVersion.V1 && isVersion1Format) || (version === WorkspaceVersion.V2 && isVersion2Format)) {
    return workspace;
  }

  // Split and reformat the workspace string
  if (version === WorkspaceVersion.V2) {
    return workspace.replace('-', '/');
  } else {
    return workspace.replace('/', '-');
  }
};

/**
 * Returns the org role with the highest privilege
 */
export const getOrgRoleFromHierarchy = (orgRoles: OrgRole | OrgRole[]): OrgRole | undefined => {
  let orgRole: string | undefined;

  if (!Array.isArray(orgRoles)) {
    orgRole = orgRoles;

    return orgRole as OrgRole;
  }

  // Hierarchically ordered org roles
  const orgRolePriority: OrgRole[] = [
    OrgRole.OWNER,
    OrgRole.ADMIN,
    OrgRole.IAM_ADMIN,
    OrgRole.BILLING_READER,
    OrgRole.USER,
  ];

  const rolesSet = new Set(orgRoles);

  for (const priorityRole of orgRolePriority) {
    if (rolesSet.has(priorityRole)) {
      orgRole = priorityRole;
      break;
    }
  }

  return orgRole as OrgRole | undefined;
};

/**
 * Matches an IDP group that ends with a system role
 */
const systemRoleGroupRegex = /^(?:.+[-_\s])?(cloud_[a-z]+(?:_[a-z]+)*)$/i;

export const mapGroupsToSystemRoles = (groups: string[]): SystemRole[] => {
  const systemRoles = groups.reduce((acc, group) => {
    const match = systemRoleGroupRegex.exec(group);
    if (match?.length) acc.push(match[1] as SystemRole);
    return acc;
  }, [] as SystemRole[]);
  return Array.from(new Set(systemRoles));
};
